import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import {
  Bell, Store, Briefcase, CreditCard, HandCoins, Hourglass, Check, CheckCheck,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
} from "./ui/dropdown-menu";

const TYPE_META = {
  shop_signup: { icon: Store, color: "text-emerald-400", label: "Shop signup" },
  distributor_signup: { icon: Briefcase, color: "text-sky-400", label: "Distributor signup" },
  payment_submitted: { icon: CreditCard, color: "text-amber-400", label: "Payment to verify" },
  commission_pending: { icon: HandCoins, color: "text-amber-400", label: "Commission pending" },
  shop_inactive: { icon: Hourglass, color: "text-red-400", label: "Inactive shop" },
};

export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [listR, countR] = await Promise.all([
        api.get("/admin/notifications", { params: { limit: 15 } }),
        api.get("/admin/notifications/unread-count"),
      ]);
      setItems(listR.data);
      setUnread(countR.data.unread || 0);
    } catch (err) {
      // Polling loop — log so silent infra issues surface in DevTools but
      // keep polling because transient blips are expected.
      console.warn("[notifications] refresh failed:", err?.message || err);
    }
  }, []);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 60000); // poll every 60s
    return () => clearInterval(id);
  }, [refresh]);

  const markRead = async (ids) => {
    if (!ids.length) return;
    await api.post("/admin/notifications/mark-read", { ids });
    refresh();
  };

  const markAllRead = async () => {
    await api.post("/admin/notifications/mark-all-read");
    refresh();
  };

  const ago = (iso) => {
    const ms = Date.now() - new Date(iso).getTime();
    const m = Math.floor(ms / 60000);
    if (m < 1) return "now";
    if (m < 60) return `${m}m`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h`;
    return `${Math.floor(h / 24)}d`;
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Notifications"
          data-testid="admin-notif-bell"
          className="relative rounded-md p-2 text-slate-300 hover:text-white hover:bg-slate-800 transition"
        >
          <Bell className="w-5 h-5" />
          {unread > 0 && (
            <span
              data-testid="admin-notif-badge"
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] rounded-full bg-red-500 text-white text-[10px] font-bold grid place-items-center px-1"
            >
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-[360px] p-0 bg-[#151A23] border border-slate-700 text-slate-200"
        data-testid="admin-notif-dropdown"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700">
          <div className="font-display font-bold">Notifications</div>
          {unread > 0 && (
            <button
              onClick={markAllRead}
              data-testid="admin-notif-mark-all"
              className="inline-flex items-center gap-1 text-xs font-semibold text-sky-300 hover:text-sky-200"
            >
              <CheckCheck className="w-3.5 h-3.5" /> Mark all read
            </button>
          )}
        </div>
        <div className="max-h-[420px] overflow-y-auto">
          {items.length === 0 && (
            <div className="px-4 py-10 text-center text-slate-500 text-sm">No notifications yet.</div>
          )}
          {items.map((n) => {
            const meta = TYPE_META[n.type] || { icon: Bell, color: "text-slate-400", label: n.type };
            return (
              <Link
                key={n.id}
                to={n.link || "#"}
                onClick={() => { setOpen(false); if (!n.read) markRead([n.id]); }}
                data-testid={`admin-notif-item-${n.id}`}
                className={`flex gap-3 px-4 py-3 border-b border-slate-800 hover:bg-slate-800/60 transition ${n.read ? "opacity-70" : ""}`}
              >
                <div className={`w-8 h-8 shrink-0 rounded-full grid place-items-center bg-slate-800 ${meta.color}`}>
                  <meta.icon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">{meta.label}</div>
                    <div className="text-[10px] text-slate-500 tabular">{ago(n.created_at)}</div>
                  </div>
                  <div className="text-sm font-semibold text-white truncate">{n.title}</div>
                  {n.body && <div className="text-xs text-slate-400 mt-0.5 line-clamp-2">{n.body}</div>}
                </div>
                {!n.read && <span className="mt-1 w-2 h-2 rounded-full bg-sky-400 shrink-0" />}
              </Link>
            );
          })}
        </div>
        <Link
          to="/admin/notifications"
          onClick={() => setOpen(false)}
          data-testid="admin-notif-view-all"
          className="block text-center text-sm font-semibold text-sky-300 hover:text-sky-200 py-3 border-t border-slate-700"
        >
          View all <Check className="w-0 h-0 hidden" />
        </Link>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
